/**
 * Plan and subscription helpers (aligned with MyPlan.vue rules).
 */

/** Message shown when a service is not included in the contracted plan. */
export const SERVICE_UNAVAILABLE_FOR_PLAN_LABEL = 'Indisponível para o plano contratado';

/**
 * Plan is active when there is a subscription and it is paid.
 * @param {{ paid?: boolean } | null | undefined} subscription
 * @returns {boolean}
 */
export const isPlanActive = (subscription) => Boolean(subscription && subscription.paid === true);

export const getFirstOrderItem = (response) =>
  response?.data?.items?.[0] ?? response?.items?.[0] ?? null;

export const pickFirstPlan = (planResponse) => {
  const plans = planResponse?.items ?? (Array.isArray(planResponse) ? planResponse : []);
  return plans[0] ?? null;
};

export const filterOccupationsByPlan = ({ occupations, planOccupations, isPrivateContext }) => {
  if (!isPrivateContext) return occupations;
  if (!planOccupations?.length) return [];
  const normalized = planOccupations.map((item) => item.toLowerCase().trim());
  return occupations.filter((item) => normalized.includes(item.toLowerCase().trim()));
};

export const syncCurrentPlanFromActiveOrder = async ({
  getSubscription,
  getAllPlans,
  setCurrentSubscription,
  setCurrentPlan,
  isPrivateContext = false,
  onUnpaid,
}) => {
  const subscription = getFirstOrderItem(await getSubscription({ status: 'ativo', limit: 1 }));
  setCurrentSubscription(subscription);

  if (isPrivateContext && !isPlanActive(subscription)) {
    if (onUnpaid) onUnpaid();
    return { subscription, plan: null };
  }

  const identifier = subscription?.iuguPlanIdentifier;
  if (!identifier) {
    setCurrentPlan(null);
    return { subscription, plan: null };
  }

  const plan = pickFirstPlan(await getAllPlans({ limit: 1, identifier }));
  setCurrentPlan(plan);
  return { subscription, plan };
};

/**
 * Whether the queue (pronto atendimento / "Entrar na fila") is allowed for the given service type.
 * @param {string} [serviceType] - 'pa' | 'eletivo' | 'ambos'
 * @returns {boolean}
 */
export const canUseQueue = (serviceType) => serviceType === 'pa' || serviceType === 'ambos';

/**
 * Whether scheduled appointment (eletivo / "Agendar consulta") is allowed for the given service type.
 * @param {string} [serviceType] - 'pa' | 'eletivo' | 'ambos'
 * @returns {boolean}
 */
export const canUseSchedule = (serviceType) => serviceType === 'eletivo' || serviceType === 'ambos';

const MONTH_WORDS_PT = {
  1: 'um',
  2: 'dois',
  3: 'três',
  4: 'quatro',
  5: 'cinco',
  6: 'seis',
  7: 'sete',
  8: 'oito',
  9: 'nove',
  10: 'dez',
  11: 'onze',
  12: 'doze',
  13: 'treze',
  14: 'quatorze',
  15: 'quinze',
  16: 'dezesseis',
  17: 'dezessete',
  18: 'dezoito',
  19: 'dezenove',
  20: 'vinte',
  21: 'vinte e um',
  22: 'vinte e dois',
  23: 'vinte e três',
  24: 'vinte e quatro',
};

const DEFAULT_CONTRACT_MONTHS = 12;

export const getPlanDurationMonths = (plan) => {
  const raw = Number(plan?.interval);
  if (!Number.isFinite(raw) || raw < 1) return DEFAULT_CONTRACT_MONTHS;
  return Math.floor(raw);
};

export const formatDurationInMonthsPt = (months) => {
  const count = Number(months);
  const safeCount = Number.isFinite(count) && count >= 1 ? Math.floor(count) : DEFAULT_CONTRACT_MONTHS;
  const word = MONTH_WORDS_PT[safeCount];
  const numericLabel = word ? `${safeCount} (${word})` : `${safeCount}`;
  const unit = safeCount === 1 ? 'mês' : 'meses';
  return `${numericLabel} ${unit}`;
};
